import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { getAllGroupsMessage } from "../../../services/groupServices";
import { sendGroupMessage } from "../../../services/messageServices";

import { IoIosSend } from "react-icons/io";
import { FaSpinner, FaTimes, FaUsers } from "react-icons/fa";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "clean",
];

const SUBJECT_OPTIONS = [
  "Adhésion",
  "Dossier d'inscription",
  "Règlement",
  "Informations cours",
  "Absence",
  "Inscription compétition",
  "Perte d'objet",
  "Autre (saisir)",
];

export default function CreateMessageGroupForm({ userId, onHide, onSent }) {
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [sending, setSending] = useState(false);

  const [groupId, setGroupId] = useState("");
  const [subjectChoice, setSubjectChoice] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function fetchGroups() {
      try {
        setLoadingGroups(true);

        const fetched = await getAllGroupsMessage();

        if (!cancelled) {
          setGroups(Array.isArray(fetched) ? fetched : []);
        }
      } catch (error) {
        console.error("Erreur groupes:", error);
        toast.error("Erreur lors de la récupération des groupes.");
      } finally {
        if (!cancelled) {
          setLoadingGroups(false);
        }
      }
    }

    fetchGroups();

    return () => {
      cancelled = true;
    };
  }, []);

  const finalSubject =
    subjectChoice === "Autre (saisir)"
      ? customSubject.trim()
      : subjectChoice.trim();

  const canSubmit = useMemo(() => {
    return (
      Boolean(groupId) &&
      Boolean(finalSubject) &&
      Boolean(content) &&
      content !== "<p><br></p>"
    );
  }, [content, finalSubject, groupId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      toast.error("Veuillez renseigner le groupe, l'objet et le contenu.");
      return;
    }

    try {
      setSending(true);

      await sendGroupMessage({
        sender_id: userId,
        group_id: groupId,
        subject: finalSubject,
        content,
      });

      toast.success("Message groupé envoyé avec succès.");
      onSent?.();
      onHide?.();
    } catch (error) {
      console.error("Envoi message groupé:", error);
      toast.error("Erreur lors de l'envoi du message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-lg shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/10">
      <div className="bg-slate-950 px-4 py-4 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10">
              <FaUsers />
            </div>

            <div>
              <p className="text-base font-bold">Nouveau message groupé</p>
              <p className="mt-0.5 text-xs text-slate-300">
                Sélectionne un groupe puis rédige ton message.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onHide}
            disabled={sending}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 transition hover:bg-white/20 disabled:opacity-60"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-5">
        <div>
          <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
            Groupe
          </label>

          {loadingGroups ? (
            <div className="h-12 w-full animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          ) : (
            <select
              value={groupId}
              onChange={(event) => setGroupId(event.target.value)}
              className="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              required
            >
              <option value="">Choisissez un groupe</option>

              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.nom}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[260px_minmax(0,1fr)]">
          <div>
            <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
              Objet
            </label>

            <select
              value={subjectChoice}
              onChange={(event) => setSubjectChoice(event.target.value)}
              className="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              required
            >
              <option value="">Sélectionnez une option</option>

              {SUBJECT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {subjectChoice === "Autre (saisir)" && (
            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
                Objet personnalisé
              </label>

              <input
                type="text"
                value={customSubject}
                onChange={(event) => setCustomSubject(event.target.value)}
                placeholder="Ex. : Rappel, information importante…"
                className="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
            Contenu
          </label>

          <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900 [&_.ql-editor]:min-h-[180px] [&_.ql-toolbar]:border-0 [&_.ql-container]:border-0">
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Rédigez votre message à destination du groupe…"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onHide}
            disabled={sending}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={sending || loadingGroups || !canSubmit}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? <FaSpinner className="animate-spin" /> : <IoIosSend />}
            {sending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </form>
    </section>
  );
}